import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useOrg } from '../../contexts/OrgContext'
import { useAuth } from '../../contexts/AuthContext'

/**
 * Unified account switcher for Global Admin users.
 * Shows a dropdown with all orgs + "Global Admin Portal" option.
 * Works in collapsed (icon only) and expanded (full dropdown) modes.
 */
function AccountSwitcher({ collapsed = false, currentView = 'admin' }) {
  const navigate = useNavigate()
  const { profile } = useAuth()
  const { orgs, currentOrg, switchOrg } = useOrg()
  const [pendingSwitch, setPendingSwitch] = useState(null)

  const isGlobalAdmin = profile?.role === 'global_admin'
  if (!isGlobalAdmin) return null

  // Determine current selection value
  const currentValue = currentView === 'global' ? '__global__' : (currentOrg?.id || '')

  // Navigate once org context has switched
  useEffect(() => {
    if (pendingSwitch && pendingSwitch === '__global__') {
      setPendingSwitch(null)
      navigate('/global')
    } else if (pendingSwitch && currentOrg?.id === pendingSwitch) {
      setPendingSwitch(null)
      navigate('/')
    }
  }, [currentOrg?.id, pendingSwitch, navigate])

  const handleChange = (value) => {
    if (value === '__global__') {
      setPendingSwitch('__global__')
      navigate('/global')
    } else if (value !== currentOrg?.id || currentView === 'global') {
      switchOrg(value)
      localStorage.setItem('ssa-active-org-id', value)
      setPendingSwitch(value)
    }
  }

  if (collapsed) {
    return (
      <div className="px-2 py-1">
        <button
          onClick={() => handleChange('__global__')}
          className="flex items-center justify-center w-full px-0 py-2.5 rounded-lg transition-all duration-200"
          style={{ color: '#7E8F9C' }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#BCC5CD' }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#7E8F9C' }}
          title="Global Admin Portal"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </button>
      </div>
    )
  }

  return (
    <div className="px-4 pb-1">
      {/* Global Admin Portal button */}
      <button
        onClick={() => handleChange('__global__')}
        className="w-full flex items-center px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 mb-2"
        style={{ color: '#7E8F9C' }}
        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#213C4E'; e.currentTarget.style.color = '#BCC5CD' }}
        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#7E8F9C' }}
      >
        <span className="mr-3 flex-shrink-0" style={{ color: '#7E8F9C' }}>
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </span>
        Global Admin Portal
        <span className="ml-auto w-1.5 h-1.5 rounded-full" style={{ backgroundColor: '#8AD0D6' }} />
      </button>

      {/* Org switcher */}
      <p className="px-3 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#5A7080' }}>
        Switch Organization
      </p>
      <select
        value={currentView === 'global' ? '' : (currentOrg?.id || '')}
        onChange={(e) => { if (e.target.value) handleChange(e.target.value) }}
        className="w-full px-3 py-2 text-[13px] font-medium rounded-lg border-0 cursor-pointer focus:ring-1 focus:ring-[#8AD0D6] focus:outline-none"
        style={{ backgroundColor: '#0D1B2A', color: '#BCC5CD' }}
      >
        {currentView === 'global' && <option value="">Select organization...</option>}
        {orgs.map(org => (
          <option key={org.id} value={org.id}>
            {org.name}{org.plan_tier === 'demo' ? ' [Demo]' : ''}
          </option>
        ))}
      </select>
    </div>
  )
}

export default AccountSwitcher
